import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

function resolveBaseUrl(): string {
  let base = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "https://api.yocava.com";

  // Strip trailing /api/v1 or /api/ followed by version to avoid double paths
  base = base.replace(/\/api\/[^/]+$/, "");

  return base;
}

function resolveApiVersion(): string {
  const explicit = import.meta.env.VITE_API_VERSION as string | undefined;
  if (explicit) return explicit;
  return "v1";
}

export const BASE_URL = resolveBaseUrl();
export const API_VERSION = resolveApiVersion();
export const API_BASE_URL = `${BASE_URL}/api/${API_VERSION}`;

const TOKEN_KEY = "CavaYo:auth-token";

export const tokenStore = {
  get(): string | null {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(TOKEN_KEY);
  },
  set(token: string) {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(TOKEN_KEY, token);
  },
  clear() {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(TOKEN_KEY);
  },
};

const getCsrfTokenFromCookie = (): string | null => {
  const name = "XSRF-TOKEN=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const cookies = decodedCookie.split(";");

  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(name) === 0) {
      return cookie.substring(name.length);
    }
  }
  return null;
};

const csrfAxios = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
  withCredentials: true,
});

let csrfTokenFetched = false;

api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = tokenStore.get();
    if (token && !config.headers?.Authorization) {
      config.headers = (config.headers || {}) as InternalAxiosRequestConfig["headers"];
      config.headers.Authorization = `Bearer ${token}`;
    }

    const needsCsrf = ["post", "put", "patch", "delete"].includes(
      config.method?.toLowerCase() || "",
    );

    if (needsCsrf && !csrfTokenFetched) {
      try {
        await csrfAxios.get("/sanctum/csrf-cookie");
        csrfTokenFetched = true;
      } catch {
        // no-op: request will continue and fail if backend requires csrf
      }
    }

    if (needsCsrf) {
      const csrfToken = getCsrfTokenFromCookie();
      if (csrfToken) {
        (config.headers as InternalAxiosRequestConfig["headers"])["X-XSRF-TOKEN"] = csrfToken;
      }
    }

    /*
      A file upload must not carry this client's default JSON content type.

      The instance sets `Content-Type: application/json` for every request.
      Axios only strips that for FormData when no explicit default exists, so a
      multipart body went up labelled as JSON: PHP never populated `$_FILES`,
      and the server answered "the document field is required" for a file that
      had been sent perfectly well. Deleting the header lets the browser write
      `multipart/form-data` with the boundary it just generated.

      This is why `lib/upload.ts` reaches for a bare axios call instead of this
      client. Fixed here so the next upload does not have to know that.
    */
    if (typeof FormData !== "undefined" && config.data instanceof FormData) {
      delete (config.headers as Record<string, unknown>)["Content-Type"];
      delete (config.headers as Record<string, unknown>)["content-type"];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

let unauthorizedHandler: (() => void) | null = null;
export const setUnauthorizedHandler = (handler: (() => void) | null) => {
  unauthorizedHandler = handler;
};

api.interceptors.response.use(
  (r) => r,
  (error: AxiosError) => {
    if (error.response?.status === 419) {
      csrfTokenFetched = false;
    }

    if (error.response?.status === 401) {
      tokenStore.clear();
      unauthorizedHandler?.();
    }
    return Promise.reject(error);
  },
);

export interface ApiErrorShape {
  message: string;
  errors?: Record<string, string[]>;
}

export function extractErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as ApiErrorShape | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (first?.[0]) return first[0];
    }
    if (data?.message) return data.message;
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return fallback;
}
